import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../config/config.service';
import { ThemeService } from './theme.service';
import { BrowserStorageService } from '../../utils/services/browser-storage.service';
import { TenantIdService } from './tenant-id.service';
import { TenantWordingService } from './tenant-wording.service';
import { TenantFeatureService } from './tenant-feature.service';
import { TenantAssetService } from './tenant-asset.service';
import { TenantThemeApiResponse } from './tenant-theme.interface';

const TENANT_LOGO_KEY = 'plp_tenant_logo';
const TENANT_FAVICON_KEY = 'plp_tenant_favicon';
const TENANT_RESPONSE_CACHE_KEY = 'plp_tenant_response';
const TENANT_CDN_URL_KEY = 'plp_tenant_cdn_url';

@Injectable({ providedIn: 'root' })
export class TenantThemeService {
  constructor(
    private http: HttpClient,
    private themeService: ThemeService,
    private browserStorage: BrowserStorageService,
    private tenantIdService: TenantIdService,
    private wordingService: TenantWordingService,
    private featureService: TenantFeatureService,
    private assetService: TenantAssetService,
    private configService: ConfigService,
    @Inject(PLATFORM_ID) private platformId: object,
    @Inject(DOCUMENT) private document: Document
  ) {}

  async loadTenantTheme(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    // Wording estático desde config.json: aplica antes del API. Sirve como fuente
    // única cuando el backend de tenants no existe, y como fallback estable cuando
    // está caído. Si el API responde con wording, lo sobreescribe más abajo.
    const staticWording = this.configService.staticWording;
    if (staticWording && Object.keys(staticWording).length > 0) {
      this.wordingService.apply(staticWording);
      if (staticWording.appName) {
        this.document.title = staticWording.appName;
      }
    }

    if (!this.configService.tenantThemeEnabled) return;

    const tenantId = this.tenantIdService.resolve();
    if (!tenantId) {
      console.warn('TenantThemeService: No tenantId resolved, using defaults.');
      return;
    }

    let data: TenantThemeApiResponse | null = null;

    try {
      const baseUrl = this.configService.baseUrl;
      const url = `${baseUrl}/api/v1/tenants/${tenantId}/theme/`;
      data = await firstValueFrom(
        this.http.get<TenantThemeApiResponse>(url)
      );
      this.cacheResponse(data);
    } catch (error) {
      console.warn('TenantThemeService: API call failed, trying cache.', error);
      data = this.loadCachedResponse();
    }

    if (!data) {
      console.warn('TenantThemeService: No data from API or cache, using defaults.');
      return;
    }

    this.applyTokens(data);
    this.applyAssets(data);
    this.applyWording(data);
    this.applyFeatures(data);
  }

  // ── Section handlers ───────────────────────────────────────

  private applyTokens(data: TenantThemeApiResponse): void {
    if (data.tokens) {
      this.themeService.applyTheme(data.tokens);
    }
  }

  private applyAssets(data: TenantThemeApiResponse): void {
    if (data.assets) {
      this.assetService.apply(data.assets);
    }
    const faviconUrl = this.assetService.getFaviconUrl();
    if (faviconUrl) {
      this.updateFavicon(faviconUrl);
    }
  }

  private applyWording(data: TenantThemeApiResponse): void {
    if (data.wording) {
      this.wordingService.apply(data.wording);
      if (data.wording.appName) {
        this.document.title = data.wording.appName;
      }
    }
  }

  private applyFeatures(data: TenantThemeApiResponse): void {
    if (data.features) {
      this.featureService.apply(this.normalizeFeatureKeys(data.features));
    }
  }

  /**
   * Normalizes backend feature keys (e.g. "showTransactions") to the
   * frontend convention (e.g. "transactions"), while keeping keys that
   * already match (e.g. "showReports", "showExport").
   */
  private normalizeFeatureKeys(raw: Record<string, boolean>): Record<string, boolean> {
    const result: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(raw)) {
      result[key] = value;
      if (key.startsWith('show') && key.length > 4) {
        const unprefixed = key.charAt(4).toLowerCase() + key.slice(5);
        result[unprefixed] = value;
      }
    }
    return result;
  }

  // ── Favicon ────────────────────────────────────────────────

  private updateFavicon(url: string): void {
    const existing = this.document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    const link = existing ?? this.document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = url;
    if (!existing) {
      this.document.head.appendChild(link);
    }
  }

  // ── Response cache ─────────────────────────────────────────

  private cacheResponse(data: TenantThemeApiResponse): void {
    try {
      this.browserStorage.setLocalStorageItem(
        TENANT_RESPONSE_CACHE_KEY,
        JSON.stringify(data)
      );
    } catch (e) {
      console.warn('TenantThemeService: Failed to cache response', e);
    }
  }

  private loadCachedResponse(): TenantThemeApiResponse | null {
    try {
      const cached = this.browserStorage.getLocalStorageItem(TENANT_RESPONSE_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      console.warn('TenantThemeService: Failed to load cached response', e);
      return null;
    }
  }

  // ── Public accessors ───────────────────────────────────────

  /** @deprecated Use TenantAssetService.getLogoUrl() directly. */
  getTenantLogoUrl(): string {
    return this.assetService.getLogoUrl();
  }

  /** @deprecated Use TenantAssetService.getFaviconUrl() directly. */
  getTenantFaviconUrl(): string | null {
    return this.assetService.getFaviconUrl();
  }

  clearTenantTheme(): void {
    this.themeService.resetToDefaults();
    this.wordingService.reset();
    this.featureService.reset();
    this.assetService.reset();
    // Clean up localStorage keys
    this.browserStorage.removeLocalStorageItem(TENANT_LOGO_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_FAVICON_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_RESPONSE_CACHE_KEY);
    this.browserStorage.removeLocalStorageItem(TENANT_CDN_URL_KEY);
  }
}
